/**
 * Mobile calendar tokens. Values come from the mobile design handoff
 * (prompts/booking_flow_redesign/mobile/wireframes/design_handoff_calendar_mobile:
 * README "Design tokens" + design-system-calendar-mobile.md §3). GRID_TOKENS
 * is the single source for every scaled grid value on the surface.
 */

export interface MobileGridTokens {
  /** Day view pixels per minute. */
  ppmDay: number;
  /** Week view pixels per minute. */
  ppmWeek: number;
  /** Day view provider column width once the row scrolls (3+ providers). */
  staffColW: number;
  /** Hour label font size (px). */
  hourLabelPx: number;
}

/**
 * Grid scale (the only density). 2 px/min in both views: a 120px hour, so a
 * phone shows about 5 hours at a time (the design's Comfortable 1.0 showed
 * about 10; doubled after review). Day and week must stay equal: they share
 * one scroll container, so the same scrollTop is the same time of day.
 */
export const GRID_TOKENS: MobileGridTokens = {
  ppmDay: 2.0,
  ppmWeek: 2.0,
  staffColW: 148,
  hourLabelPx: 10.5,
};

/** Sticky hour rail width. */
export const HOUR_RAIL_W = 46;

/** Side-by-side blocks allowed in a week column before the `+N` chip. */
export const WEEK_OVERLAP_CAP = 2;

/** Minimum rendered block heights (px). */
export const DAY_BLOCK_MIN_H = 26;
export const WEEK_BLOCK_MIN_H = 22;

/**
 * z-index scale for mobile overlays.
 *
 * In-surface layers (grid, slot menu, FAB) live inside the
 * surface root, which is its own stacking context (`isolation: isolate`), so
 * their values never compete with portals.
 *
 * Portaled layers (bottom sheets, nav drawer) sit BELOW Mantine's defaults
 * (modal 200, popover/combobox 300), so Mantine modals opened from the
 * calendar (Appointment Details, time-block modal, legacy create modal,
 * confirmations) and any Select/Menu dropdown always render above a sheet.
 */
export const MOBILE_Z = {
  gridHeader: 22,
  hourRail: 12,
  nowLine: 10,
  slotMenuBackdrop: 40,
  slotMenu: 50,
  fabScrim: 42,
  fabStack: 44,
  fab: 46,
  /** Pushed full screen (rebook) over the grid, FAB and slot menu. */
  pushedScreen: 60,
  /** Toast above the floating layer and a pushed screen. */
  toast: 70,
  /** Portaled: nav drawer + bottom sheets (below Mantine modal 200). */
  sheet: 150,
  /** A sheet's full-screen nested screen (Forms / Notes / messages). */
  sheetScreen: 160,
} as const;

/** Status chip colors (bg / ink / border) from the design seed data. */
export interface StatusTone {
  label: string;
  bg: string;
  fg: string;
  bd: string;
}

const tone = (label: string, token: string): StatusTone => ({
  label,
  bg: `var(--status-${token}-bg)`,
  fg: `var(--status-${token}-ink)`,
  bd: `var(--status-${token}-border)`,
});

/** Values live in the shared `--status-*` tokens (src/index.css). */
export const STATUS_TONES: Record<string, StatusTone> = {
  Requested: tone("Requested", "requested"),
  Accepted: tone("Accepted", "accepted"),
  Confirmed: tone("Confirmed", "confirmed"),
  // Brand lime ramp (STATUS_COLORS.Arrived).
  Arrived: {
    label: "Arrived",
    bg: "var(--mantine-color-limeGreen-1)",
    fg: "var(--mantine-color-limeGreen-9)",
    bd: "var(--mantine-color-limeGreen-3)",
  },
  Completed: tone("Completed", "completed"),
  "No Show": tone("No show", "no-show"),
  Declined: tone("Declined", "closed"),
  Cancelled: tone("Cancelled", "closed"),
};

/**
 * Display-only "Accepted (Unconfirmed)" chip (Accepted within 48h of start;
 * see guards.appointmentStatusDisplay): deep orange from the brand burntOrange
 * ramp (ACCEPTED_UNCONFIRMED_COLOR is burntOrange.8).
 */
export const ACCEPTED_UNCONFIRMED_TONE: StatusTone = {
  label: "Accepted (Unconfirmed)",
  bg: "var(--mantine-color-burntOrange-1)",
  fg: "var(--mantine-color-burntOrange-9)",
  bd: "var(--mantine-color-burntOrange-8)",
};

/** Fallback service hue (design `color()` fallback for unknown services). */
export const FALLBACK_HUE = "#8a8a8a";
